import { Component, computed, inject, Input, model, signal } from '@angular/core';
import { InspectionItemDto, InspectionService, SortDto } from '@artemis-software/wr-api';
import { Tracked } from '@/utils/signals/tracked';
import { asyncComputed } from '@/utils/signals/asyncComputed';
import { watch } from '@/utils/signals/watch';
import { currentTenantId } from '@/utils/admin-utils';
import { InspectionDashboardFilter } from '@pages/inspection-dashboard-page/inspection-dashboard-filter/inspection-dashboard-filter.component';
import { InspectionDashboardMode } from '@pages/inspection-dashboard-page/inspection-dashboard-mode';
import { general_tab } from '@pages/inspection-detail-page-v2/inspection-tab.constants';

@Component({
  standalone: false,
  selector: 'wr-inspection-dashboard-list',
  templateUrl: './inspection-dashboard-list.component.html',
  styleUrls: ['./inspection-dashboard-list.component.scss'],
})
export class InspectionDashboardListComponent {
  private readonly inspectionService = inject(InspectionService);

  readonly currentTenantId = currentTenantId();

  @Input()
  @Tracked()
  filter?: InspectionDashboardFilter;

  readonly mode = model<InspectionDashboardMode>('overdue');

  readonly upcomingLoading = signal(false);
  readonly overdueLoading = signal(false);

  readonly upcomingPage = model<number>(0);
  readonly upcomingPageSize = model<number>(10);
  readonly upcomingSort = model<SortDto>({ sort: 'fromTimestamp', order: 'ASC' });

  readonly overduePage = model<number>(0);
  readonly overduePageSize = model<number>(10);

  // Only the active mode's endpoint is called - reading this.mode() here makes the
  // effect re-run (and skip the other list's request) when the toggle switches.
  private readonly upcomingResult = asyncComputed(
    () => {
      if (!this.filter || this.mode() !== 'upcoming') return undefined;
      return this.inspectionService.getUpcomingInspectionsForDashboard({
        internalTenantId: this.currentTenantId(),
        tenantId: this.filter.tenantId,
        buildingId: this.filter.buildingId,
        teamId: this.filter.teamId,
        regionId: this.filter.regionId,
        technicianReference: this.filter.technicianReference,
        clerkReference: this.filter.clerkReference,
        assignedToCurrentUser: this.filter.assignedToCurrentUser,
        sort: this.upcomingSort(),
        page: this.upcomingPage(),
        size: this.upcomingPageSize(),
      });
    },
    undefined,
    this.upcomingLoading,
  );

  readonly upcomingInspections = computed(() => this.upcomingResult()?.items ?? []);
  readonly upcomingCount = computed(() => this.upcomingResult()?.totalItems ?? 0);

  private readonly overdueResult = asyncComputed(
    () => {
      if (!this.filter || this.mode() !== 'overdue') return undefined;
      return this.inspectionService.getOverdueInspectionsForDashboard({
        ownerTenantId: this.filter.tenantId,
        buildingId: this.filter.buildingId,
        teamId: this.filter.teamId,
        regionId: this.filter.regionId,
        hideNeverInspected: this.filter.hideNeverInspected,
        page: this.overduePage(),
        size: this.overduePageSize(),
      });
    },
    undefined,
    this.overdueLoading,
  );

  readonly overdueInspections = computed(() => this.overdueResult()?.items ?? []);
  readonly overdueCount = computed(() => this.overdueResult()?.totalItems ?? 0);

  constructor() {
    watch(
      () => this.filter,
      () => {
        this.upcomingPage.set(0);
        this.overduePage.set(0);
      },
    );
  }

  readonly getInspectionLink = (inspection: InspectionItemDto) => (['inspection', inspection.id, general_tab]);
}
